/**
 * The company dashboard.
 *
 * What each listing has cost, what it has returned — views and outbound clicks
 * — and the actions that matter: finish a reservation, renew (free), and raise
 * (how a listing climbs). Raising opens the bid modal over the page rather
 * than pushing the table down.
 *
 * This is also the only place an unpublished listing appears anywhere, and
 * there are now two kinds of them. A **reservation** is paid for and has no
 * details yet: it is finished, not published, and its row leads with that. A
 * **draft** is the older shape — details written, payment never completed —
 * which the old listing-first flow could produce and which is still
 * completable through the same checkout. Neither is linked to a public page,
 * because neither has one.
 */

import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, type ReactNode } from 'react';

import {
  JOB_STATUS_LABELS,
  LISTING_LIFETIME_DAYS,
  MINIMUM_FIRST_BID_CENTS,
  getSubcategory,
  hasListingContent,
  isAwaitingFirstPayment,
  isPaidAwaitingContent,
  type Job,
  type LeaderboardScope,
  type MyListing,
} from '@bidtohire/shared';

import { BidModal } from '../components/BidPanel';
import { CloseAccountDialog, CloseAccountSection } from '../components/CloseAccount';
import { CompanyForm } from '../components/CompanyForm';
import { JobForm } from '../components/JobForm';
import { Money } from '../components/Money';
import {
  BoardSkeleton,
  CautionNotice,
  EmptyState,
  ErrorNotice,
  SuccessNotice,
  errorMessage,
} from '../components/Status';
import { PublishedBeat, forgetPublishing, readPublishing } from '../components/PublishedBeat';
import { CompanyMark } from '../components/ui';
import { PUBLISH_FROM_LABEL } from '../lib/bid-pricing';
import { daysRemaining, formatDate, isExpired } from '../lib/format';
import { jobFormFrom } from '../lib/job-form';
import { useAuth } from '../lib/auth-context';
import {
  useDeleteCompany,
  useDeleteJob,
  useMyJobs,
  useRenewJob,
  useSaveCompany,
  useUpdateJob,
} from '../lib/queries';

/**
 * The four states a row can be in, which are the 2×2 of paid and written with
 * the published half collapsed into one.
 */
type RowKind = 'live' | 'draft' | 'reserved-paid' | 'reserved-unpaid';

function rowKind(listing: MyListing): RowKind {
  if (!hasListingContent(listing)) {
    return isPaidAwaitingContent(listing) ? 'reserved-paid' : 'reserved-unpaid';
  }
  return isAwaitingFirstPayment(listing) ? 'draft' : 'live';
}

function StatusBadge({ listing }: { readonly listing: MyListing }): ReactNode {
  const kind = rowKind(listing);
  // Nothing unpublished is reported by the calendar. Its sixty days have not
  // started, so a lapsed or absent date is not an expiry and must not be shown
  // as one.
  if (kind === 'reserved-paid') {
    return (
      <span className="badge badge-indigo">{JOB_STATUS_LABELS.PAID_AWAITING_CONTENT}</span>
    );
  }
  if (kind === 'reserved-unpaid') {
    return <span className="badge badge-neutral">{JOB_STATUS_LABELS.PENDING_CONTENT}</span>;
  }
  if (kind === 'draft') return <span className="badge badge-caution">Not published</span>;
  if (isExpired(listing as Job)) {
    return <span className="badge badge-caution">{JOB_STATUS_LABELS.EXPIRED}</span>;
  }
  if (listing.status === 'ACTIVE') {
    return <span className="badge badge-ok">{JOB_STATUS_LABELS.ACTIVE}</span>;
  }
  return <span className="badge badge-neutral">{JOB_STATUS_LABELS[listing.status]}</span>;
}

export function DashboardPage(): ReactNode {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const query = useMyJobs();
  const renew = useRenewJob();
  const remove = useDeleteJob();
  const updateJob = useUpdateJob();
  const saveCompany = useSaveCompany();
  const closeAccount = useDeleteCompany();

  const [bidding, setBidding] = useState<string | undefined>(undefined);
  const [editing, setEditing] = useState<string | undefined>(undefined);
  const [editingCompany, setEditingCompany] = useState(false);
  const [closing, setClosing] = useState(false);
  // Read once, and cleared straight away: the beat belongs to this return from
  // Stripe, not to every later visit to the dashboard.
  const [justPaid] = useState<string | undefined>(() => {
    const id = readPublishing();
    forgetPublishing();
    return id;
  });

  if (query.isLoading) {
    return (
      <div className="inner" style={{ paddingTop: 32 }}>
        <BoardSkeleton rows={4} />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="inner" style={{ paddingTop: 32 }}>
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      </div>
    );
  }
  if (query.data === undefined) return null;

  const { company, jobs } = query.data;
  const bidOutcome = params.get('bid');
  const justPublished = params.get('published') === '1';
  // Neither return from a payment carries a listing id, so the panel that sent
  // the browser away left one behind.
  const celebrating =
    bidOutcome === 'complete' || justPublished
      ? jobs.find((job) => job.id === justPaid)
      : undefined;
  const celebrated = celebrating !== undefined && hasListingContent(celebrating)
    ? celebrating
    : undefined;

  const found = jobs.find((job) => job.id === editing);
  const editingJob = found !== undefined && hasListingContent(found) ? found : undefined;
  const bidFound = jobs.find((job) => job.id === bidding);
  const biddingJob = bidFound !== undefined && hasListingContent(bidFound) ? bidFound : undefined;

  const reservations = jobs.filter((job) => rowKind(job) === 'reserved-paid');
  const drafts = jobs.filter((job) => rowKind(job) === 'draft');
  const totalViews = jobs.reduce((sum, job) => sum + job.viewCount, 0);
  const totalClicks = jobs.reduce((sum, job) => sum + job.applyClickCount, 0);

  return (
    <>
      <div style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--canvas-soft)' }}>
        <div className="inner" style={{ paddingTop: 36, paddingBottom: 30 }}>
          <div className="row" style={{ gap: 14, alignItems: 'center' }}>
            <CompanyMark company={company} size="md" />
            <div>
              <h1 style={{ fontSize: 28 }}>{company.name}</h1>
              <p className="small muted" style={{ marginTop: 4 }}>
                <Link to={`/companies/${company.slug}`}>View public profile</Link> ·{' '}
                <button
                  type="button"
                  className="linkbtn"
                  onClick={() => setEditingCompany(!editingCompany)}
                >
                  {editingCompany ? 'Close company profile' : 'Edit company profile'}
                </button>
              </p>
            </div>
            <span className="spacer" />
            <Link className="pill pill-primary" to="/post">
              Post another job
            </Link>
          </div>

          <div className="statgrid" style={{ marginTop: 24 }}>
            <div className="stat">
              <span className="eyebrow">Total paid</span>
              <div className="value money" style={{ color: 'var(--indigo-deep)' }}>
                <Money cents={company.totalSpendCents} />
              </div>
              <div className="note">Cumulative and non-refundable</div>
            </div>
            <div className="stat">
              <span className="eyebrow">Listings</span>
              <div className="value money">{jobs.length}</div>
              <div className="note">
                {LISTING_LIFETIME_DAYS}-day term, renewable free
              </div>
            </div>
            <div className="stat">
              <span className="eyebrow">Listing views</span>
              <div className="value money">{totalViews.toLocaleString('en-US')}</div>
              <div className="note">Across every listing</div>
            </div>
            <div className="stat">
              <span className="eyebrow">Clicks to your site</span>
              <div className="value money">{totalClicks.toLocaleString('en-US')}</div>
              <div className="note">Every application starts on your own posting</div>
            </div>
          </div>
        </div>
      </div>

      <section className="section is-last" style={{ paddingTop: 28 }}>
        <div className="inner stack" style={{ gap: 20 }}>
          {bidOutcome === 'complete' || justPublished ? (
            celebrated === undefined ? (
              <SuccessNotice>
                {justPublished
                  ? 'Your listing is published.'
                  : 'Payment received. The position updates when Stripe confirms it.'}
              </SuccessNotice>
            ) : (
              <PublishedBeat
                job={celebrated}
                company={company}
                {...(justPublished
                  ? {
                      headline: 'Your listing is live.',
                      note: 'It was paid for when you reserved it, so it joined the board the moment you submitted the details.',
                    }
                  : {})}
                onPostAnother={() => void navigate('/post')}
              />
            )
          ) : null}
          {bidOutcome === 'cancelled' ? (
            <CautionNotice>Payment cancelled. Nothing was charged.</CautionNotice>
          ) : null}

          {editingCompany ? (
            <CompanyForm
              company={company}
              submitLabel="Save profile"
              busy={saveCompany.isPending}
              serverError={saveCompany.isError ? errorMessage(saveCompany.error) : undefined}
              onSubmit={async (input) => {
                await saveCompany.mutateAsync(input);
                setEditingCompany(false);
              }}
            />
          ) : null}

          {renew.isError ? <ErrorNotice error={renew.error} /> : null}
          {remove.isError ? <ErrorNotice error={remove.error} /> : null}
          {renew.isSuccess && renew.data !== undefined ? (
            <SuccessNotice>
              Renewed. “{renew.data.job.title}” now runs until{' '}
              {formatDate(renew.data.validThrough)} — {LISTING_LIFETIME_DAYS} days from today, at no
              cost, at the position it holds.
            </SuccessNotice>
          ) : null}

          {reservations.length > 0 ? (
            <CautionNotice>
              {reservations.length === 1
                ? 'One listing is paid and waiting for its details.'
                : `${reservations.length} listings are paid and waiting for their details.`}{' '}
              Add the posting and it publishes immediately. A reservation does not expire.
            </CautionNotice>
          ) : null}

          {drafts.length > 0 ? (
            <CautionNotice>
              {drafts.length === 1
                ? 'One listing was never paid for.'
                : `${drafts.length} listings were never paid for.`}{' '}
              A first bid of <Money cents={MINIMUM_FIRST_BID_CENTS} /> or more publishes it. Until
              then it is a private draft only you can see.
            </CautionNotice>
          ) : null}

          {jobs.length === 0 ? (
            <EmptyState
              headline="You have no listings yet."
              action={
                <Link className="pill pill-primary" to="/post">
                  Post a job — {PUBLISH_FROM_LABEL}
                </Link>
              }
            >
              <Money cents={MINIMUM_FIRST_BID_CENTS} /> reserves a listing and buys its position.
              Paste your posting afterwards and we fill in the details.
            </EmptyState>
          ) : (
            <div className="card" style={{ padding: '16px 0 0', overflow: 'hidden' }}>
              <div className="tablewrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Listing</th>
                      <th scope="col">Status</th>
                      <th scope="col" className="numeric">
                        Paid
                      </th>
                      <th scope="col" className="numeric">
                        Views
                      </th>
                      <th scope="col" className="numeric">
                        Clicks
                      </th>
                      <th scope="col">Runs until</th>
                      <th scope="col">
                        <span className="visually-hidden">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((listing) => {
                      const kind = rowKind(listing);
                      const content = hasListingContent(listing) ? listing : undefined;
                      const expired = kind === 'live' && isExpired(listing as Job);
                      const name = content?.title ?? 'Reserved listing';
                      return (
                        <tr key={listing.id}>
                          <th scope="row">
                            {kind === 'live' ? (
                              <Link to={`/jobs/${listing.id}`} style={{ fontWeight: 500 }}>
                                {name}
                              </Link>
                            ) : (
                              // Not a link: nothing unpublished has a public
                              // page, and offering one would lead to a 404.
                              <span style={{ fontWeight: 500 }}>{name}</span>
                            )}
                            <div className="small muted">
                              {content === undefined
                                ? 'No details yet'
                                : (getSubcategory(content.category, content.subcategory)?.name ??
                                  content.subcategory)}
                            </div>
                          </th>
                          <td>
                            <StatusBadge listing={listing} />
                          </td>
                          <td className="numeric">
                            {kind === 'reserved-unpaid' ? (
                              <>
                                <Money cents={listing.reservedAmountCents ?? 0} />
                                <div className="small muted">Pending</div>
                              </>
                            ) : (
                              <Money cents={listing.totalPaidCents} />
                            )}
                          </td>
                          <td className="numeric">{listing.viewCount.toLocaleString('en-US')}</td>
                          <td className="numeric">
                            {listing.applyClickCount.toLocaleString('en-US')}
                          </td>
                          <td>
                            {content === undefined || kind === 'draft' ? (
                              <>
                                —
                                <div className="small muted">Starts when you publish</div>
                              </>
                            ) : (
                              <>
                                {formatDate(content.validThrough)}
                                <div className="small muted">
                                  {expired
                                    ? 'Expired'
                                    : `${daysRemaining(content.validThrough)} days left`}
                                </div>
                              </>
                            )}
                          </td>
                          <td>
                            <div className="actions">
                              {kind === 'reserved-paid' ? (
                                <Link
                                  className="linkbtn is-primary"
                                  to={`/post/finish?job=${listing.id}`}
                                >
                                  Add the posting
                                </Link>
                              ) : null}
                              {kind === 'reserved-unpaid' ? (
                                <Link className="linkbtn is-primary" to="/post">
                                  Finish reserving
                                </Link>
                              ) : null}
                              {/* An expired listing is on no board, so there is
                                  no position to bid for and the server refuses
                                  the checkout. Renewal is free and restores the
                                  position, so it leads instead. */}
                              {kind === 'draft' || (kind === 'live' && !expired) ? (
                                <button
                                  type="button"
                                  className={kind === 'draft' ? 'linkbtn is-primary' : 'linkbtn'}
                                  onClick={() => setBidding(listing.id)}
                                >
                                  {kind === 'draft' ? 'Complete your bid to publish' : 'Raise bid'}
                                </button>
                              ) : null}
                              {kind === 'live' ? (
                                <button
                                  type="button"
                                  className={expired ? 'linkbtn is-primary' : 'linkbtn'}
                                  disabled={renew.isPending && renew.variables === listing.id}
                                  onClick={() => {
                                    renew.reset();
                                    renew.mutate(listing.id);
                                  }}
                                >
                                  {expired ? 'Renew — free, to bid again' : 'Renew'}
                                </button>
                              ) : null}
                              {content === undefined ? null : (
                                <button
                                  type="button"
                                  className="linkbtn"
                                  onClick={() => {
                                    renew.reset();
                                    setEditing(editing === listing.id ? undefined : listing.id);
                                  }}
                                >
                                  Edit
                                </button>
                              )}
                              <button
                                type="button"
                                className="linkbtn is-danger"
                                disabled={remove.isPending}
                                onClick={() => {
                                  const confirmed = window.confirm(
                                    kind === 'reserved-paid'
                                      ? 'Remove this reservation? You have already paid for it, and that payment is not refundable. Nothing is returned.'
                                      : kind === 'live'
                                        ? `Remove “${name}”? It leaves every board immediately. Payments already made are not refunded.`
                                        : `Remove “${name}”? It was never published.`,
                                  );
                                  if (!confirmed) return;
                                  remove.mutate({
                                    id: listing.id,
                                    ...(kind === 'reserved-paid' ? { forfeit: true } : {}),
                                  });
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="board-foot">
                <span>
                  A payment of <Money cents={MINIMUM_FIRST_BID_CENTS} /> or more reserves a listing
                  and buys its position; it publishes as soon as you add the posting. Published
                  listings run {LISTING_LIFETIME_DAYS} days and renew free. The total paid and the
                  position carry over.
                </span>
              </div>
            </div>
          )}

          {editingJob === undefined ? null : (
            <section className="stack" style={{ gap: 12 }}>
              <div className="section-heading" style={{ marginBottom: 0 }}>
                <div>
                  <h2>Edit {editingJob.title}</h2>
                  <p>Editing never affects a listing’s position or the date it was posted.</p>
                </div>
                <button type="button" className="linkbtn" onClick={() => setEditing(undefined)}>
                  Cancel
                </button>
              </div>
              <JobForm
                initial={jobFormFrom(editingJob)}
                submitLabel="Save changes"
                busy={updateJob.isPending}
                serverError={updateJob.isError ? errorMessage(updateJob.error) : undefined}
                footerNote={
                  isAwaitingFirstPayment(editingJob) ? (
                    <>
                      Nothing is charged yet. Saving keeps this a draft — publish it with “Complete
                      your bid to publish”, from <Money cents={MINIMUM_FIRST_BID_CENTS} />.
                    </>
                  ) : (
                    <>
                      Saved edits go live right away. This does not change the position, the amount
                      already paid, or the {LISTING_LIFETIME_DAYS}-day run.
                    </>
                  )
                }
                onSubmit={async (input) => {
                  await updateJob.mutateAsync({ id: editingJob.id, input });
                  setEditing(undefined);
                }}
              />
            </section>
          )}

          <CloseAccountSection onOpen={() => setClosing(true)} />
        </div>
      </section>

      {!closing ? null : (
        <CloseAccountDialog
          companyName={company.name}
          jobCount={jobs.length}
          busy={closeAccount.isPending}
          error={closeAccount.isError ? closeAccount.error : undefined}
          signInStranded={closeAccount.data?.signInRemoved === false}
          onCancel={() => {
            closeAccount.reset();
            setClosing(false);
          }}
          onConfirm={() => {
            void (async () => {
              const outcome = await closeAccount.mutateAsync();
              // Everything asked for is deleted either way, but a sign-in that
              // outlived it means the address is not free to register again.
              // Signing out silently would leave that discovered at the sign-up
              // screen, with nothing to explain it.
              if (outcome?.signInRemoved === false) return;
              // The account is gone; the session it was reached with has to go
              // with it, or the next page load asks a deleted profile for its
              // listings.
              auth.signOut();
              void navigate('/', { replace: true });
            })().catch(() => {
              // Reported inside the dialog, which stays open so it can be
              // retried without retyping the name.
            });
          }}
        />
      )}

      {biddingJob === undefined ? null : (
        <BidModal
          job={biddingJob}
          scope={
            {
              kind: 'subcategory',
              category: biddingJob.category,
              subcategory: biddingJob.subcategory,
            } satisfies LeaderboardScope
          }
          scopeName={
            getSubcategory(biddingJob.category, biddingJob.subcategory)?.name ??
            biddingJob.subcategory
          }
          onClose={() => setBidding(undefined)}
        />
      )}
    </>
  );
}
